import { z } from 'zod';
import { AuditedFields, IsoDateTime, Uuid, Version } from './common.js';
/** Caisse (E-12 / FR-PAY-05) — sessions et mouvements d'espèces. */
export const CashSessionStatus = z.enum(['OPEN', 'CLOSED']);
export const CashMovementKind = z.enum(['IN', 'OUT', 'EXPENSE', 'ADJUSTMENT', 'PAYMENT', 'REFUND']);
export const CashMovement = z.object({
    id: Uuid,
    sessionId: Uuid,
    kind: CashMovementKind,
    amount: z.number(),
    /** Signe appliqué au solde : +1 entrée, −1 sortie. */
    direction: z.enum(['IN', 'OUT']),
    category: z.string().nullable(),
    description: z.string(),
    paymentId: Uuid.nullable(),
    paymentReference: z.string().nullable(),
    occurredAt: IsoDateTime,
    createdByName: z.string().nullable(),
});
export const CashSession = z
    .object({
    id: Uuid,
    propertyId: Uuid,
    status: CashSessionStatus,
    openedByName: z.string().nullable(),
    openedAt: IsoDateTime,
    openingBalance: z.number(),
    closedByName: z.string().nullable(),
    closedAt: IsoDateTime.nullable(),
    expectedBalance: z.number(),
    actualBalance: z.number().nullable(),
    variance: z.number().nullable(),
    varianceJustification: z.string().nullable(),
    reconciledByName: z.string().nullable(),
    reconciledAt: IsoDateTime.nullable(),
    totals: z.object({
        payments: z.number(),
        refunds: z.number(),
        ins: z.number(),
        outs: z.number(),
        expenses: z.number(),
        movementsCount: z.number().int(),
    }),
    notes: z.string().nullable(),
    version: Version,
})
    .merge(AuditedFields);
export const OpenCashSessionRequest = z.object({
    propertyId: Uuid.optional(),
    openingBalance: z.number().nonnegative(),
    notes: z.string().max(500).nullable().optional(),
});
export const CloseCashSessionRequest = z.object({
    actualBalance: z.number().nonnegative(),
    notes: z.string().max(1000).nullable().optional(),
    version: Version,
});
export const ReconcileCashSessionRequest = z.object({
    justification: z.string().min(5).max(1000),
    version: Version,
});
export const RecordCashMovementRequest = z.object({
    kind: CashMovementKind.exclude(['PAYMENT', 'REFUND']),
    amount: z.number().positive(),
    /**
     * Sens du mouvement. Déduit du `kind` (`IN` entre, `OUT`/`EXPENSE` sortent) ;
     * seul un `ADJUSTMENT` doit le préciser, une correction pouvant aller dans
     * les deux sens. Par défaut un ajustement est une entrée.
     */
    direction: z.enum(['IN', 'OUT']).optional(),
    category: z.string().max(60).nullable().optional(),
    description: z.string().min(2).max(300),
    occurredAt: IsoDateTime.optional(),
});
export const ListCashSessionsQuery = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(25),
    cursor: z.string().max(512).optional(),
    propertyId: Uuid.optional(),
    status: CashSessionStatus.optional(),
    from: IsoDateTime.optional(),
    to: IsoDateTime.optional(),
});
/** Synthèse pour l'écran Caisse : session ouverte (ou null) et derniers mouvements. */
export const CashOverview = z.object({
    currentSession: CashSession.nullable(),
    recentMovements: z.array(CashMovement),
    todayCashPayments: z.number(),
});
//# sourceMappingURL=cash.js.map